"use client";
import { logger } from "@/utils/logger";

import { JSXElementConstructor, Key, ReactElement, ReactNode, ReactPortal, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "@/components/ui/Button";
import { ChevronRight, ArrowDown, Building2, TrendingDown, Target, ShieldCheck, TrendingUp, Phone, Mail, FolderOpen } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { EmptyState } from "@/components/ui/EmptyState";
import { apiClient } from "@/lib/apiClient";
import { useSettings } from "@/context/SettingsContext";

export default function OurProjects() {
  const settings = useSettings();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState<any[]>([]);
  const filteredProjects = projects;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projRes, capRes] = await Promise.all([
          apiClient.get('/public/projects') as any,
          apiClient.get('/public/capabilities') as any
        ]);
        setProjects(projRes.data || []);
        setCapabilities(capRes.data || []);
      } catch (error) {
        logger.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const icon1Ref = useRef<HTMLDivElement>(null);
  const icon2Ref = useRef<HTMLDivElement>(null);


  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Floating animations
    if (blob1Ref.current) gsap.to(blob1Ref.current, { y: -15, rotation: 5, duration: 4, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (blob2Ref.current) gsap.to(blob2Ref.current, { y: 15, duration: 3.5, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (icon1Ref.current) gsap.to(icon1Ref.current, { y: -20, rotation: 10, duration: 4, yoyo: true, repeat: -1, ease: "sine.inOut" });
    if (icon2Ref.current) gsap.to(icon2Ref.current, { y: 20, rotation: -10, duration: 4.5, yoyo: true, repeat: -1, ease: "sine.inOut", delay: 1 });


  }, []);

  useEffect(() => {
    let lastHash = typeof window !== 'undefined' ? window.location.hash : '';

    const handleHashChange = () => {
      if (typeof window !== 'undefined' && window.location.hash) {
        const id = window.location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
    };

    // Listen to native hashchange for in-page navigation (like footer links)
    window.addEventListener('hashchange', handleHashChange);

    // Fallback interval for cached Next.js transitions from other pages
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.location.hash !== lastHash) {
        lastHash = window.location.hash;
        handleHashChange();
      }
    }, 500);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!loading && filteredProjects.length > 0) {
      // Scroll to hash if present in URL (on initial load)
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (typeof window !== 'undefined' && window.location.hash) {
          const id = window.location.hash.substring(1);
          const element = document.getElementById(id);
          if (element) {
            // Scroll to the element
            const y = element.getBoundingClientRect().top + window.scrollY - 120;
            window.scrollTo({ top: y, behavior: 'smooth' });

            // If we successfully found and tried to scroll, clear interval
            // But we'll try a couple of times just in case layout shifts
            if (attempts >= 3) {
              clearInterval(interval);
            }
          }
        }

        // Failsafe: stop checking after 10 attempts (5 seconds)
        if (attempts > 10) clearInterval(interval);
      }, 500);

      const ctx = gsap.context(() => {
        gsap.utils.toArray(".project-card").forEach((el: any, idx) => {
          gsap.fromTo(el,
            { opacity: 0, y: 40 },
            { opacity: 1, y: 0, duration: 0.7, scrollTrigger: { trigger: el, once: true, start: "top 85%" } }
          );
        });

        // Premium GSAP animations for placeholders
        gsap.to(".gsap-abstract-1", {
          y: -20, x: -10, rotation: 45, scale: 1.1, duration: 6, repeat: -1, yoyo: true, ease: "sine.inOut"
        });
        gsap.to(".gsap-abstract-2", {
          y: 20, x: 15, rotation: -30, scale: 0.9, duration: 7, repeat: -1, yoyo: true, ease: "sine.inOut"
        });
        gsap.to(".gsap-abstract-3", {
          x: -30, y: -10, scale: 1.2, opacity: 0.5, duration: 8, repeat: -1, yoyo: true, ease: "sine.inOut"
        });
        gsap.to(".gsap-abstract-4", {
          rotation: 180, x: 20, y: 20, duration: 10, repeat: -1, yoyo: true, ease: "sine.inOut"
        });

        // Gentle pulse on the icon
        gsap.to(".gsap-badge-icon", {
          y: -5, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut"
        });
      }, containerRef);
      return () => {
        clearInterval(interval);
        ctx.revert();
      };
    }
  }, [loading, filteredProjects]);

  return (
    <div className="min-h-screen bg-brand-white font-sans overflow-x-hidden pt-28 pb-0">

      {/* 1. CREATIVE CURVED HERO */}
      <section className="relative min-h-[450px] flex flex-col items-center justify-center text-center px-6 mb-12 max-w-7xl mx-auto">

        {/* Animated Curved Elements */}
        <div
          ref={blob1Ref}
          className="absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 hidden md:block"
        >
          <svg width="150" height="150" viewBox="0 0 100 100" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" className="opacity-30">
            <path d="M10,90 Q40,40 90,10" />
            <path d="M20,90 Q50,50 90,20" strokeDasharray="3 3" />
            <circle cx="90" cy="10" r="3" fill="#0d9488" />
            <circle cx="10" cy="90" r="2" fill="#0d9488" />
          </svg>
        </div>

        <div
          ref={blob2Ref}
          className="absolute right-4 lg:right-10 top-[30%] hidden md:block"
        >
          <svg width="120" height="120" viewBox="0 0 100 100" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" className="opacity-30">
            <path d="M10,50 C30,10 70,90 90,50" />
            <circle cx="50" cy="50" r="4" fill="none" />
            <path d="M40,50 L60,50" />
            <path d="M50,40 L50,60" />
          </svg>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">
          <span className="text-[#0f766e] font-sans font-bold text-[10px] md:text-[11px] tracking-widest uppercase mb-4 block">
            Proven Results
          </span>

          <div className="relative mb-8">
            <h1 className="text-[40px] md:text-[56px] font-serif font-extrabold tracking-tight text-gray-900 leading-tight">
              Our Projects
            </h1>
            <svg className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-32 md:w-48 h-6 text-[#0d9488]/30 -z-10" viewBox="0 0 100 20" preserveAspectRatio="none">
              <path d="M 5 15 Q 50 5 95 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>

          <p className="font-sans text-gray-500 text-[14px] md:text-[15px] max-w-2xl leading-relaxed font-medium mb-10">
            Explore the details of our recent case records and discover how we have helped clients drastically reduce costs and improve their operational efficiency.
          </p>
        </div>
      </section>



      {/* 3. SHOWCASE CASE STUDIES (Light Colorful Image Collage Layout) */}
      <section className="relative w-full py-16 md:py-20 mt-2">

        {/* Solid Light Teal Background covering the section */}
        <div className="absolute inset-0 bg-gradient-to-r from-white to-[#f0fdfa] z-0"></div>
        {/* Decorative Grid */}
        <div className="absolute inset-0 bg-[radial-gradient(#0d9488_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.07] z-0"></div>

        {/* Unique Wavy Top Border */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(-1px)' }}>
          <svg className="relative block w-full h-[30px] md:h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#ffffff"></path>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative z-10" ref={containerRef}>
          {loading ? (
            <div
              key="loading"
              className="py-24 text-center text-gray-500 font-sans text-lg animate-pulse"
            >
              Loading projects...
            </div>
          ) : filteredProjects.length === 0 ? (
            <div
              key="empty"
              className="py-12"
            >
              <EmptyState
                title="Projects Curating..."
                message="We are selecting our best case studies to showcase our impact. Check back shortly."
                icon={<FolderOpen className="w-8 h-8 text-teal-600 opacity-80" strokeWidth={1.5} />}
              />
            </div>
          ) : (
            <div className="relative z-10 flex flex-col gap-20 md:gap-28 py-6">
              {filteredProjects.map((project, index) => {
                const isEven = index % 2 === 0;

                // Professional images related to B2B services
                const imagePairs = [
                  ['https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?q=80&w=800', 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=600'],
                  ['https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=800', 'https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=600'],
                  ['https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=800', 'https://images.unsplash.com/photo-1556761175-4b46a572b786?q=80&w=600'],
                  ['https://images.unsplash.com/photo-1512820790803-83ca734da794?q=80&w=800', 'https://images.unsplash.com/photo-1553484771-371a605b060b?q=80&w=600'],
                  ['https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=800', 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=600'],
                  ['https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=800', 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=600']
                ];
                const imgs = imagePairs[index % imagePairs.length];

                const hasMainImg = project.images && project.images[0] && project.images[0].trim() !== "";
                const hasOverlapImg = project.images && project.images[1] && project.images[1].trim() !== "";
                const hasBothMissing = !hasMainImg && !hasOverlapImg;

                return (
                  <div
                    key={project.slug}
                    id={project.slug}
                    className={`project-card opacity-0 flex flex-col lg:flex-row items-center gap-12 lg:gap-20 w-full ${isEven ? '' : 'lg:flex-row-reverse'}`}
                  >
                    {/* Text Side (50%) */}
                    <div className="w-full lg:w-1/2 flex flex-col relative z-10">
                      {/* Giant Number overlapping text exactly like the screenshot */}
                      <div className={`absolute -top-16 lg:-top-24 ${isEven ? '-left-8 lg:-left-16' : '-right-8 lg:-right-16 text-right'} text-[180px] lg:text-[280px] font-serif font-black text-[#0f172a] opacity-[0.03] select-none pointer-events-none -z-10 tracking-tighter leading-none`}>
                        0{index + 1}
                      </div>

                      <div className={`flex items-center gap-4 mb-6 ${isEven ? '' : 'lg:justify-end'}`}>
                        {isEven && <div className="w-12 h-[2px] bg-[#0d9488]" />}
                        <span className="text-[#0f766e] tracking-[0.2em] uppercase text-[12px] font-bold">Project</span>
                        {!isEven && <div className="w-12 h-[2px] bg-[#0d9488] hidden lg:block" />}
                      </div>

                      <h2 className={`font-sans text-[36px] lg:text-[52px] font-extrabold text-[#0f172a] leading-[1.1] mb-8 tracking-tight ${isEven ? '' : 'lg:text-right'}`}>
                        {project.title}
                      </h2>

                      {project.clientName && (
                        <p className={`font-sans text-[16px] lg:text-[18px] text-gray-600 leading-relaxed mb-10 ${isEven ? '' : 'lg:text-right'}`}>
                          <span className="font-medium italic border-b-2 border-teal-100 pb-1">{project.clientName}</span>
                        </p>
                      )}

                      {/* Achievements as clean list */}
                      <div className={`flex flex-col gap-6 ${isEven ? '' : 'lg:items-end'}`}>
                        {project.results && project.results.map((achievement: string | number | bigint | boolean | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | Promise<string | number | bigint | boolean | ReactPortal | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | null | undefined> | null | undefined, aIdx: Key | null | undefined) => (
                          <div key={aIdx} className={`flex items-start gap-4 ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                            <div className="w-8 h-8 rounded-full bg-white shadow-[0_2px_10px_rgba(13,148,136,0.08)] flex items-center justify-center shrink-0 mt-0.5 border border-gray-50">
                              <ShieldCheck className="w-4 h-4 text-[#0d9488]" strokeWidth={2.5} />
                            </div>
                            <span className={`font-sans text-[15px] text-gray-700 leading-relaxed max-w-md ${isEven ? '' : 'lg:text-right'}`}>{achievement}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Image Side (50%) - Using Collage Design */}
                    <div className={`w-full lg:w-1/2 flex ${isEven ? 'justify-end' : 'justify-start'} relative mt-10 lg:mt-0`}>
                      <div className="w-full max-w-[500px] h-[320px] md:h-[380px] relative flex items-center justify-center group">

                        {hasBothMissing ? (
                          <div className="w-full h-full relative overflow-hidden rounded-[40px] bg-gradient-to-br from-teal-900 to-slate-900 shadow-xl border-[6px] border-white flex items-center justify-center z-10 transition-transform duration-700 group-hover:scale-[1.02]">

                            {/* Premium Animated GSAP Elements */}
                            <div className="absolute inset-0 z-0 overflow-hidden">
                              <div className="gsap-abstract-1 absolute -top-12 -right-12 w-48 h-48 rounded-full border border-teal-500/20" />
                              <div className="gsap-abstract-2 absolute top-1/4 -left-12 w-32 h-32 rounded-[20px] rotate-12 border border-teal-400/20" />
                              <div className="gsap-abstract-3 absolute -bottom-20 right-1/4 w-64 h-64 rounded-full bg-teal-500/10 blur-[40px]" />
                              <div className="gsap-abstract-4 absolute top-1/3 right-1/3 w-24 h-24 border border-teal-300/10 rounded-full" />
                            </div>

                            {/* Premium Glassmorphism Badge */}
                            <div className="z-20 bg-white/10 backdrop-blur-md px-10 py-8 rounded-[32px] border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.2)] flex flex-col items-center transition-all duration-700">
                              <div className="gsap-badge-icon w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 text-white rounded-[20px] flex items-center justify-center mb-5 shadow-[0_4px_20px_rgba(13,148,136,0.3)] border border-teal-300/30">
                                <ShieldCheck className="w-8 h-8" strokeWidth={1.5} />
                              </div>
                              <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-teal-100 font-bold mb-2">Compare All</span>
                              <span className="font-serif text-2xl text-white font-black tracking-tight text-center leading-none">Project</span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Accent curved shape behind images */}
                            <div className={`absolute top-4 ${isEven ? 'right-4 md:right-8' : 'left-4 md:left-8'} w-[75%] h-[85%] bg-[#d9f2ef] z-0 rounded-[24px]`} />

                            {/* Image 1 (Main) */}
                            {hasMainImg && (
                              <div className={`absolute ${hasOverlapImg ? 'top-0' : 'inset-0 m-auto'} ${hasOverlapImg ? (isEven ? 'left-0 rounded-tl-[40px] rounded-br-[12px] rounded-tr-[12px] rounded-bl-[12px]' : 'right-0 rounded-tr-[40px] rounded-bl-[12px] rounded-tl-[12px] rounded-br-[12px]') : 'rounded-[40px]'} ${hasOverlapImg ? 'w-[75%] h-[80%]' : 'w-[90%] h-[90%]'} shadow-lg border-[6px] border-white z-10 overflow-hidden`}>
                                <Image src={project.images[0]} fill sizes="(max-width: 1024px) 100vw, 50vw" className="object-cover group-hover:scale-105 transition-transform duration-700" alt="Main Project Image" />
                              </div>
                            )}

                            {/* Image 2 (Overlapping) */}
                            {hasOverlapImg && (
                              <div className={`absolute ${hasMainImg ? 'bottom-0' : 'inset-0 m-auto'} ${hasMainImg ? (isEven ? 'right-0 rounded-b-[40px] rounded-t-[12px]' : 'left-0 rounded-b-[40px] rounded-t-[12px]') : 'rounded-[40px]'} ${hasMainImg ? 'w-[60%] h-[60%]' : 'w-[90%] h-[90%]'} shadow-xl border-[6px] border-white z-20 overflow-hidden`}>
                                <Image src={project.images[1]} fill sizes="(max-width: 1024px) 100vw, 33vw" className="object-cover group-hover:-rotate-2 transition-transform duration-700" alt="Overlapping Detail" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 4. ELEGANT ANIMATED CTA (Unique Borderless Design - Match Services Page) */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gray-50 flex items-center justify-center">

        {/* Creative Wavy Divider from Insights Section */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-0" style={{ transform: 'translateY(-1px)' }}>
          <svg className="relative block w-full h-[40px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#f0fdfa"></path>
          </svg>
        </div>

        {/* Unique Background Node/Line Pattern */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCI+PHBhdGggZD0iTTAgNDBoODBNNDAgMHY4MCIgc3Ryb2tlPSIjMEYxNzJBIiBzdHJva2Utd2lkdGg9IjAuNSIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjQwIiBjeT0iNDAiIHI9IjMiIGZpbGw9IiMwRjE3MkEiLz48L3N2Zz4=')" }} />

        {/* Floating Abstract Icons in Open Space */}
        <div ref={icon1Ref} className="absolute top-32 left-10 md:left-32 opacity-[0.03] pointer-events-none z-0">
          <Target size={180} strokeWidth={1} />
        </div>
        <div ref={icon2Ref} className="absolute bottom-20 right-10 md:right-32 opacity-[0.03] pointer-events-none z-0">
          <TrendingUp size={180} strokeWidth={1} />
        </div>

        {/* Massive Ambient Glow (Replaces the need for a card) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle,_var(--tw-gradient-stops))] from-[#0d9488]/10 via-[#3b82f6]/5 to-transparent rounded-full blur-[100px] pointer-events-none z-0" />

        <div className="max-w-[1000px] mx-auto px-6 relative z-20 w-full flex flex-col items-center text-center">

          {/* Central Anchor Icon */}
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center shadow-[0_10px_40px_rgba(13,148,136,0.15)] mb-10 text-[#0d9488] relative z-10 border border-gray-100">
            <ShieldCheck size={32} strokeWidth={1.5} />
          </div>

          <h2 className="text-[28px] md:text-[42px] font-serif font-bold text-gray-900 leading-[1.1] mb-8 tracking-tight relative z-10 max-w-[800px]">
            All consultancy projects are <br className="hidden md:block" /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0d9488] to-[#3b82f6]">free of charge.</span>
          </h2>

          <p className="text-[16px] md:text-[18px] text-gray-500 font-sans font-medium max-w-[650px] mb-14 leading-relaxed relative z-10">
            To find out more or for a free, no obligation consultation please {settings.contactPhone?.trim() ? <>call <strong className="text-gray-800"><a href={`tel:${settings.contactPhone}`} className="hover:text-[#0d9488] transition-colors">{settings.contactPhone}</a></strong> or email us at</> : "email us at"} <strong className="text-gray-800"><a href={`mailto:${settings.contactEmail}`} className="hover:text-[#0d9488] transition-colors">{settings.contactEmail}</a></strong>
          </p>

          {/* Capability Pills - Borderless floating look */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-5 mb-16 max-w-[800px] relative z-10">
            {capabilities.length > 0 ? (
              capabilities.map((cap, idx) => {
                const stripHtml = (html: any) => (html || '').replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim();
                const sDesc = stripHtml(cap.shortDescription);
                const lDesc = stripHtml(cap.longDescription);
                const hasDetails = (sDesc.length > 0 && sDesc.toLowerCase() !== 'n/a') || (lDesc.length > 0 && lDesc.toLowerCase() !== 'n/a');

                return hasDetails ? (
                  <Link
                    key={idx}
                    href={`/capabilities/${cap.slug}`}
                    className="inline-block bg-white/60 backdrop-blur-sm border border-gray-200/60 text-gray-600 hover:text-[#0d9488] hover:bg-white hover:border-[#0d9488]/30 font-sans font-bold text-[11px] md:text-[13px] px-6 py-3 rounded-full transition-all cursor-pointer shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                  >
                    {cap.title}
                  </Link>
                ) : (
                  <span
                    key={idx}
                    className="inline-block bg-white/60 backdrop-blur-sm border border-gray-200/60 text-gray-600 hover:text-[#0d9488] hover:bg-white hover:border-[#0d9488]/30 font-sans font-bold text-[11px] md:text-[13px] px-6 py-3 rounded-full transition-all cursor-default shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                  >
                    {cap.title}
                  </span>
                );
              })
            ) : (
              ["Printing", "Mailing", "IT support", "Disaster recovery", "Space planning", "Service provision"].map((pill, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-white/60 backdrop-blur-sm border border-gray-200/60 text-gray-600 hover:text-[#0d9488] hover:bg-white hover:border-[#0d9488]/30 font-sans font-bold text-[11px] md:text-[13px] px-6 py-3 rounded-full transition-all cursor-default shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:scale-105 hover:-translate-y-0.5"
                >
                  {pill}
                </span>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
